import type { CheckoutStyle } from '@primer-io/checkout-web';
import { PSP_FONT_FAMILY, PSP_FONT_STYLESHEET, PSP_STYLE_TOKENS, PSP_STYLE_TOKENS_V3_CARE_PLUS } from './pspStyleTokens';
import type { CheckoutStyleVariant } from './checkoutFormProps';

// ============================================================================
// Style Configuration
// ============================================================================

// Card fields render in Primer iframes, so only this style option reaches them.
// 2.65.0 has no height for submitButton and no selected state for paymentMethodButton
// (only savedPaymentMethodButton.selected), so the 56px pay button / selected-row outline
// from our skin can't be matched exactly.
const { input, label, paymentMethod, payButton } = PSP_STYLE_TOKENS;

const primerStyle: CheckoutStyle = {
  stylesheets: [{ href: PSP_FONT_STYLESHEET }],
  loadingScreen: { color: PSP_STYLE_TOKENS.colorPrimary },
  processingIndicator: { color: PSP_STYLE_TOKENS.colorPrimary },
  inputLabel: {
    fontFamily: PSP_FONT_FAMILY,
    fontSize: label.fontSize,
    fontWeight: label.fontWeight,
    color: PSP_STYLE_TOKENS.colorLabel,
  },
  input: {
    base: {
      height: input.height,
      fontFamily: PSP_FONT_FAMILY,
      fontSize: input.fontSize,
      fontWeight: input.fontWeight,
      color: PSP_STYLE_TOKENS.colorText,
      background: PSP_STYLE_TOKENS.colorBackground,
      borderStyle: 'solid',
      borderWidth: input.borderWidth,
      borderColor: input.borderColor,
      borderRadius: input.borderRadius,
      focus: {
        borderColor: input.focusBorderColor,
        boxShadow: input.focusBoxShadow,
      },
    },
    error: {
      borderColor: PSP_STYLE_TOKENS.colorDanger,
    },
  },
  inputErrorText: {
    fontFamily: PSP_FONT_FAMILY,
    fontSize: label.fontSize,
    color: PSP_STYLE_TOKENS.colorDanger,
  },
  errorMessage: {
    fontFamily: PSP_FONT_FAMILY,
    color: PSP_STYLE_TOKENS.colorDanger,
    borderRadius: paymentMethod.borderRadius,
  },
  paymentMethodButton: {
    height: input.height,
    borderRadius: paymentMethod.borderRadius,
    borderStyle: 'solid',
    borderWidth: input.borderWidth,
    borderColor: input.borderColor,
    primaryText: { fontFamily: PSP_FONT_FAMILY, color: PSP_STYLE_TOKENS.colorText },
  },
  savedPaymentMethodButton: {
    base: { fontFamily: PSP_FONT_FAMILY, color: PSP_STYLE_TOKENS.colorText, borderRadius: paymentMethod.borderRadius },
    selected: { borderColor: paymentMethod.selectedColor },
  },
  submitButton: {
    base: {
      background: payButton.background,
      borderRadius: payButton.borderRadius,
      color: payButton.color,
      fontFamily: PSP_FONT_FAMILY,
      fontSize: payButton.fontSize,
      fontWeight: payButton.fontWeight,
    },
  },
};

const primerStyleV3CarePlus: CheckoutStyle = {
  ...primerStyle,
  paymentMethodButton: {
    ...primerStyle.paymentMethodButton,
    borderRadius: PSP_STYLE_TOKENS_V3_CARE_PLUS.borderRadius,
    borderColor: PSP_STYLE_TOKENS_V3_CARE_PLUS.borderColor,
  },
  savedPaymentMethodButton: {
    base: { ...primerStyle.savedPaymentMethodButton?.base, borderRadius: PSP_STYLE_TOKENS_V3_CARE_PLUS.borderRadius },
    selected: { borderColor: PSP_STYLE_TOKENS_V3_CARE_PLUS.selectedColor },
  },
};

export const PRIMER_STYLES: Record<CheckoutStyleVariant, CheckoutStyle> = {
  default: primerStyle,
  v3CarePlus: primerStyleV3CarePlus,
};
